import {
    messages as messagesApi,
    type EmailAttachment,
} from "./api";

export const MAX_EMAIL_ATTACHMENT_BYTES = 8 * 1024 * 1024;
export const MAX_EMAIL_ATTACHMENT_COUNT = 10;

export interface UploadedEmailAttachment extends EmailAttachment {
    previewUrl?: string;
}

function effectiveContentType(file: File): string {
    return file.type || "application/octet-stream";
}

export function validateAttachmentSelection(
    existing: UploadedEmailAttachment[],
    files: File[],
): void {
    if (existing.length + files.length > MAX_EMAIL_ATTACHMENT_COUNT) {
        throw new Error(`A maximum of ${MAX_EMAIL_ATTACHMENT_COUNT} attachments is allowed.`);
    }
    const total = existing.reduce((sum, attachment) => sum + attachment.size, 0)
        + files.reduce((sum, file) => sum + file.size, 0);
    if (total > MAX_EMAIL_ATTACHMENT_BYTES) {
        throw new Error("Attachments must total 8 MiB or less.");
    }
    if (files.some(file => file.size === 0)) {
        throw new Error("Empty files cannot be attached.");
    }
}

export async function uploadEmailAttachment(
    file: File,
    disposition: "attachment" | "inline",
): Promise<UploadedEmailAttachment> {
    const result = await messagesApi.prepareAttachmentUpload({
        filename: file.name,
        contentType: effectiveContentType(file),
        size: file.size,
        disposition,
    });
    if (!result.ok || !result.upload || !result.attachment) {
        throw new Error(result.error ?? "attachment_upload_prepare_failed");
    }

    const response = await fetch(result.upload.url, {
        method: result.upload.method,
        headers: result.upload.headers,
        body: file,
    });
    if (!response.ok) {
        throw new Error(`Attachment upload failed with HTTP ${response.status}.`);
    }

    return {
        ...result.attachment,
        previewUrl: disposition === "inline" ? URL.createObjectURL(file) : undefined,
    };
}

export function prepareEmailAttachmentsForSend(
    bodyHtml: string | undefined,
    uploaded: UploadedEmailAttachment[],
): { bodyHtml: string | undefined; attachments: EmailAttachment[] } {
    let html = bodyHtml;
    const attachments: EmailAttachment[] = [];

    for (const attachment of uploaded) {
        if (attachment.disposition === "inline") {
            if (!html || !attachment.previewUrl || !attachment.contentId
                || !html.includes(attachment.previewUrl)) {
                continue;
            }
            html = html.split(attachment.previewUrl).join(`cid:${attachment.contentId}`);
        }
        const { previewUrl: _previewUrl, ...sendAttachment } = attachment;
        attachments.push(sendAttachment);
    }

    return { bodyHtml: html, attachments };
}

export function revokeAttachmentPreviews(attachments: UploadedEmailAttachment[]): void {
    for (const attachment of attachments) {
        if (attachment.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
    }
}

export function removeEditorImageBySrc(editor: any, src: string | undefined): void {
    if (!editor || !src) return;
    const { state } = editor;
    const transaction = state.tr;
    const positions: Array<{ from: number; to: number }> = [];
    state.doc.descendants((node: any, position: number) => {
        if (node.type.name === "image" && node.attrs.src === src) {
            positions.push({ from: position, to: position + node.nodeSize });
        }
    });
    for (const position of positions.reverse()) {
        transaction.delete(position.from, position.to);
    }
    if (positions.length) editor.view.dispatch(transaction);
}

export function formatAttachmentSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KiB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
}
